import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import {
  AudioProcessingIntervalType,
  AudioProcessingReportResponseType,
} from '@account_module/types/AudioProcessing';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { convertSecondsToHMS, dateWithoutTimezone } from '@utils/dateUtils';
import { PROCESSING_INTERVAL } from '@account_module/constants/audioProcessing';
import { DAY_NAME_MAPPER } from '@account_module/constants/date';
import { Loader } from '@components/Loader';

const TableWrapper = tw.div`relative w-full max-h-[40vh] min-h-[40vh] overflow-y-scroll overflow-x-hidden rounded-lg bg-neutral-background`;
const Table = tw.table`w-full h-[inherit] min-h-full md:min-w-[750px] bg-page-background overflow-auto rounded-[8px] border-separate`;
const THead = tw.thead`w-full bg-page-background sticky top-0 z-[1]`;
const TBody = tw.tbody`h-full w-full`;
const TR = tw.tr`w-full border-b border-solid border-transparent py-1`;
const TH = tw.th`whitespace-nowrap border border-transparent p-2 text-text-and-icon-muted text-sm text-left`;
const TD = tw.td`whitespace-nowrap border border-transparent p-2 text-sm font-medium text-left text-text-and-icon`;
const NoDataContainer = tw.div`p-4`;

const Label = ({ label }: { label: string }) => (
  <Option tw='text-text-and-icon-muted capitalize'>{label}</Option>
);

const intervalOptions: IDropdownOption[] = Object.values(PROCESSING_INTERVAL).map(
  (interval: string) => ({ label: <Label label={interval} />, value: interval })
);

export const AudioProcessingReportTable = ({
  data,
  params,
  onChange,
  isLoading,
}: {
  data: AudioProcessingReportResponseType[];
  params: { [key: string]: Date | string | null };
  onChange: (val: AudioProcessingIntervalType) => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const intervalValue = useMemo(
    () => intervalOptions.find((opt) => opt.value === params?.interval) as IDropdownOption,
    [params]
  );

  const getIntervalString = useCallback(
    (dateStr: string) => {
      switch (intervalValue.value) {
        case PROCESSING_INTERVAL.DAY:
          return DAY_NAME_MAPPER[new Date(dateStr).getDay()];
        case PROCESSING_INTERVAL.WEEK:
          return new Date(dateStr).toLocaleString().split(',')[0];
        case PROCESSING_INTERVAL.MONTH:
          return new Date(dateStr).toLocaleString('default', { month: 'long' });
        case PROCESSING_INTERVAL.YEAR:
          return new Date(dateStr).getFullYear();
      }
    },
    [intervalValue]
  );

  return (
    <TableWrapper>
      <Table>
        <THead>
          <TR>
            <TH>
              {t('account_module.settings_page.audio_processing_report.table.thead_interval')}
              <Dropdown
                tw='shadow-none z-1 mx-1'
                options={intervalOptions}
                value={intervalValue}
                onChange={(val) => {
                  const { value } = val as IDropdownOption;
                  onChange(value as AudioProcessingIntervalType);
                }}
              />
              {isLoading ? (
                <Loader size='xs' theme='dark' tw='inline-block' />
              ) : (
                `(${data?.length})`
              )}
            </TH>
            <TH>
              {t('account_module.settings_page.audio_processing_report.table.thead_audio_count')}
            </TH>
            <TH>
              {t(
                'account_module.settings_page.audio_processing_report.table.thead_processing_time'
              )}
            </TH>
          </TR>
        </THead>
        <TBody>
          {isLoading ? (
            <TR>
              <TD colSpan={3}>
                <Loader size='md' theme='dark' />
              </TD>
            </TR>
          ) : data?.length ? (
            data?.map((item: AudioProcessingReportResponseType, index: number) => (
              <TR key={index} tw='odd:bg-neutral-background even:bg-gray-2'>
                <TD>{getIntervalString(dateWithoutTimezone(new Date(item?.interval_segment)))}</TD>
                <TD>{item?.audio_count}</TD>
                <TD>{`${convertSecondsToHMS(item?.audio_duration_sec)}h`}</TD>
              </TR>
            ))
          ) : (
            <TR>
              <TD colSpan={3} tw='text-center!'>
                <NoDataContainer>
                  {t('account_module.settings_page.audio_processing_report.no_data')}
                </NoDataContainer>
              </TD>
            </TR>
          )}
        </TBody>
      </Table>
    </TableWrapper>
  );
};
